import {useCallback, useEffect, useState} from 'react';

import {UseQueueParameters} from './useQueue.types';

/**
 * useQueue creates a local state value that represents an array of items, and
 * provides controls for modifying and reading information about the array.
 *
 * Note that items in the array will be mapped to an object that contains the
 * item under the `data` key, will additional keys providing state values
 * related to the functionality of the queue.
 */
export function useQueue<T>({initialItems = [], maxLength}: UseQueueParameters<T>) {
  const [list, setList] = useState<
    {
      data: T;
      id: string;
      willRemove: boolean;
    }[]
  >(
    initialItems.map(initialItem => ({
      data: initialItem,
      id: window.btoa(performance.now().toString()),
      willRemove: false,
    })),
  );
  const [shouldFilterList, setShouldFilterList] = useState(false);

  /**
   * Adds an item to the end of the queue. If the length of the queue with the
   * new item exceeds the `maxLength`, then the first item from the queue will
   * be flagged for removal.
   */
  const addToQueue = useCallback(
    (item: T) => {
      const newItem = {
        data: item,
        id: window.btoa(performance.now().toString()),
        willRemove: false,
      };
      const setWillRemove = typeof maxLength === 'number' && list.length === maxLength;
      const newList = setWillRemove
        ? [{...list[0], willRemove: true}, ...list.slice(1), newItem]
        : [...list, newItem];

      setList(newList);

      if (setWillRemove) {
        setShouldFilterList(true);
      }
    },
    [list, maxLength],
  );

  /**
   * Removes an item from the queue. Accepts an number indicating the index of
   * the item to be flagged for removal from the queue. By default, this number
   * is 0, meaning the first item from the queue will be removed.
   */
  const removeFromQueue = useCallback(
    (index = 0) => {
      if (list.length > 0 && !!list[index]) {
        setList(
          list.map((listItem, i) => (i === index ? {...listItem, willRemove: true} : listItem)),
        );
        setShouldFilterList(true);
      }
    },
    [list],
  );

  /**
   * Flags all items in the queue for removal.
   */
  const resetQueue = useCallback(() => {
    setList(list.map(listItem => ({...listItem, willRemove: true})));
  }, [list]);

  useEffect(() => {
    // This hook runs when `shouldFilterList` is set and, after a delay, updates
    // the list to filter out any entries that have been flagged for removal
    // with a `willRemove` property. This is done to allow the UI to use the
    // `willRemove` flag to play any "exit" animations necessary before
    // the array is updated and any removed items can no longer be rendered.
    if (shouldFilterList) {
      const filteredList = list.filter(({willRemove}) => !willRemove);
      const timeoutId = setTimeout(() => {
        setList(filteredList);
        setShouldFilterList(false);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [list, shouldFilterList]);

  const controls = {
    addToQueue,
    length: list.length,
    removeFromQueue,
    resetQueue,
  };

  return [list, controls] as const;
}
